import isServer from 'common/util/isServer';

import type { RouteComponent, RouterState } from 'react-router';

type ComponentCallback = (error: any, component: RouteComponent) => void;

export class ImportTracker {
  trackedImports: Set<string> = new Set();

  getTrackedImports = () => {
    return [...this.trackedImports];
  };

  track = (importPath: string) => {
    if (!isServer()) {
      throw new Error('Should never track imports on the client');
    }
    this.trackedImports.add(importPath);
  };
}

/**
 * A helper for creating dynamic imports for routes.
 *
 * The componentLoader must statically define an directory when `import`ing. Webpack uses it when
 * deciding what files are related. Using a variable in the `import()` call will result in Webpack
 * being unable to statically identify the appropriate files and cause unnecessarily linking in the
 * bundle. This can cause bugs and bloat the bundle.
 *
 * @param importTracker - The tracker to use, used by the server to determine what chunk files to serve
 * @param componentLoader - The function used to lazily load the route files
 */
export function createDynamicImport(
  importTracker: ImportTracker | undefined,
  componentLoader: (path: string) => Promise<any>,
) {
  return (filename: string, { fromShared = false }: { fromShared?: boolean } = {}) => {
    const dynamicLoader = (_: RouterState<any>, cb: ComponentCallback) => {
      const extension = isServer() ? '.js' : '';
      const importPath = `${filename}${extension}`;
      importTracker?.track(importPath);
      const loader = fromShared
        ? import(`../routeComponents/general/${importPath}`)
        : componentLoader(importPath);

      loader.then((module) => {
        cb(null, isServer() && module?.default?.default ? module.default.default : module.default);
      });
    };
    dynamicLoader.isDynamicLoader = true;
    return dynamicLoader;
  };
}
